'use client';

import { type ReactNode, createContext, useContext, useEffect } from 'react';

import { useMediaQueries } from '@commons/contexts/responsive';
import PageBuilder from '@commons/pagebuilder';
import type { Page } from '@commons/services/page';

// Type definition for the page context.

type PageContextType = {
  /**
   * Boolean to determine if the page is being previewed.
   */
  isPreviewing?: boolean;

  /**
   * Boolean to determine if the editor is active on the client.
   */
  isEditing: boolean;
};

/**
 * Context to store the page options and functions.
 *
 * @see https://reactjs.org/docs/context.html
 */

const PageContext = createContext<PageContextType>({
  isPreviewing: false,
  isEditing: false,
});

/**
 * Custom hook to access the page context.
 *
 * @returns the page options and functions.
 */

export const usePageContext = (): PageContextType => useContext(PageContext);

// Type for the provider properties.

type Props = {
  children: ReactNode;
  page?: Page;
  isPreviewing?: boolean;
  isEditing?: boolean;
};

/**
 * Page context provider.
 *
 * Sets if the page data, and if it is being previewed or edited in the context.
 *
 * Also triggers global side effects such as scrollbar width calculation.
 *
 * @see https://www.smashingmagazine.com/2023/12/new-css-viewport-units-not-solve-classic-scrollbar-problem/
 */

export const PageProvider = ({
  children,
  page,
  isPreviewing = false,
  isEditing = false,
}: Props) => {
  // Sets the page data in the PageBuilder instance so it can be accessed everywhere (on the client).
  PageBuilder.setPage(page);

  // Computes the scrollbar width and update the viewport width CSS variable.
  const { ready } = useMediaQueries();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const root = document.documentElement;
      const scrollbarWidth = window.innerWidth - root.clientWidth;
      root.style.setProperty('--viewport-width', `calc(100vw - ${scrollbarWidth}px)`);
    }
  }, [ready]);

  return <PageContext value={{ isPreviewing, isEditing }}>{children}</PageContext>;
};

export default PageProvider;
